import { Alert, Badge, Box, Button, Card, Divider, Group, Modal, Skeleton, Stack, Text, Textarea, ThemeIcon, Title } from "@mantine/core";
import { IconAlertCircle, IconArrowBack, IconFileText } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import { ChargeContractApi } from "../../../apis";
import type { ChargeContract, ComponentInput } from "../../../apis/chargeContract";
import { ConflictReloadModal } from "../../../components/billing/ConflictReloadModal";
import { ComponentTypeIcon, componentTypeLabel, orderTypeLabel } from "../../../components/billing/ComponentTypeIcon";
import { ContractStatusBadge } from "../../../components/billing/ContractStatusBadge";
import { MoneyCell } from "../../../components/billing/MoneyCell";
import { PermissionGate } from "../../../components/billing/PermissionGate";
import { SectionHeader } from "../../../components/billing/SectionHeader";
import { TenantName } from "../../../components/billing/TenantName";
import { PageLayout } from "../../../components/layout";
import { useOptimisticMutation } from "../../../hooks/useOptimisticMutation";
import { formatDate, formatPercent } from "../../../utils/format";

type ContractComponent = ComponentInput & { id: string };

const heroWash = { background: "light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-6))" };

const ContractDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: contract, mutate, isLoading } = useSWR<ChargeContract | null>(
    id ? `swr.billing.contract.${id}` : null,
    () => (id ? ChargeContractApi.get(id) : null)
  );
  const [busy, setBusy] = useState(false);
  const [termOpen, setTermOpen] = useState(false);
  const [termReason, setTermReason] = useState("");
  const { conflict, clearConflict, run } = useOptimisticMutation();

  const breadcrumbs = [
    { to: "/", label: "Эхлэл" },
    { to: "/billing/contracts", label: "Гэрээ" },
    { label: contract?.contractNumber ?? "..." }
  ];

  const act = async (fn: () => Promise<unknown>, errMsg: string) => {
    if (!contract) return;
    setBusy(true);
    try {
      const res = await run(fn);
      if (res.ok) await mutate();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      // eslint-disable-next-line no-alert
      alert(`${errMsg}: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  if (isLoading || !contract) {
    return (
      <PageLayout title="Гэрээ" breadcrumb={breadcrumbs}>
        <Skeleton height={240} radius="md" />
      </PageLayout>
    );
  }

  const isDraft = contract.status === "DRAFT";
  const isActive = contract.status === "ACTIVE";
  const isSuspended = contract.status === "SUSPENDED";
  const isFinal = contract.status === "TERMINATED" || contract.status === "ARCHIVED";

  return (
    <PageLayout
      title={contract.contractName}
      subTitle={contract.contractNumber}
      breadcrumb={breadcrumbs}
      extra={[
        <Group key="actions" gap="xs">
          <Button
            variant="subtle"
            leftSection={<IconArrowBack size={16} />}
            onClick={() => navigate("/billing/contracts")}
          >
            Жагсаалт руу
          </Button>
          <PermissionGate code="BIL_002">
            {isDraft && (
              <>
                <Button
                  variant="filled"
                  color="brand"
                  disabled={busy}
                  onClick={() => act(() => ChargeContractApi.activate(contract.id, contract.rowVersion), "Идэвхжүүлэхэд алдаа")}
                >
                  Идэвхжүүлэх
                </Button>
                <Button
                  variant="default"
                  disabled={busy}
                  onClick={() => act(() => ChargeContractApi.cancel(contract.id, contract.rowVersion), "Цуцлахад алдаа")}
                >
                  Цуцлах
                </Button>
              </>
            )}
            {isActive && (
              <>
                <Button
                  variant="default"
                  disabled={busy}
                  onClick={() => act(() => ChargeContractApi.suspend(contract.id, contract.rowVersion), "Зогсооход алдаа")}
                >
                  Түр зогсоох
                </Button>
                <Button variant="outline" color="red" disabled={busy} onClick={() => setTermOpen(true)}>
                  Дуусгах
                </Button>
              </>
            )}
            {isSuspended && (
              <>
                <Button
                  variant="filled"
                  color="brand"
                  disabled={busy}
                  onClick={() => act(() => ChargeContractApi.resume(contract.id, contract.rowVersion), "Сэргээхэд алдаа")}
                >
                  Сэргээх
                </Button>
                <Button variant="outline" color="red" disabled={busy} onClick={() => setTermOpen(true)}>
                  Дуусгах
                </Button>
              </>
            )}
          </PermissionGate>
        </Group>
      ]}
    >
      <Stack gap="lg">
        {isFinal && (
          <Alert color="gray" icon={<IconAlertCircle size={16} />}>
            Гэрээ {contract.status === "TERMINATED" ? "цуцлагдсан" : "архивлагдсан"}. Засварлах боломжгүй.
          </Alert>
        )}

        <Card withBorder padding="lg" radius="md" style={heroWash}>
          <Stack gap="md">
            <Group justify="space-between" align="flex-start">
              <Group gap="sm" wrap="nowrap">
                <ThemeIcon variant="light" color="brand" size={44} radius="md">
                  <IconFileText size={24} />
                </ThemeIcon>
                <Box>
                  <Text fw={700} fz="xl" style={{ fontVariantNumeric: "tabular-nums" }}>{contract.contractNumber}</Text>
                  <Text size="sm" c="dimmed">{contract.contractName}</Text>
                </Box>
              </Group>
              <ContractStatusBadge status={contract.status} />
            </Group>
            <Divider />
            <Group gap="xl" wrap="wrap">
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>Тенант</Text>
                <TenantName id={contract.tenantId} />
              </Box>
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>Валют</Text>
                <Text fw={500}>{contract.currency}</Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>Үйлчилгээ эхэлсэн</Text>
                <Text fw={500} style={{ fontVariantNumeric: "tabular-nums" }}>{formatDate(contract.effectiveFrom)}</Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>Үйлчилгээ дуусах</Text>
                <Text fw={500} style={{ fontVariantNumeric: "tabular-nums" }}>{contract.effectiveUntil ? formatDate(contract.effectiveUntil) : "—"}</Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>Хувилбар</Text>
                <Badge variant="default">{contract.rowVersion}</Badge>
              </Box>
            </Group>
            {contract.terminationReason && (
              <>
                <Divider />
                <Box>
                  <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>Цуцлах шалтгаан</Text>
                  <Text>{contract.terminationReason}</Text>
                </Box>
              </>
            )}
          </Stack>
        </Card>

        <Stack gap="sm">
          <SectionHeader eyebrow="Гэрээний нөхцөл" title={`Бүрэлдэхүүн (${contract.components.length})`} />
          {contract.components.map((c: ContractComponent) => (
            <Card key={c.id} withBorder padding="md" radius="md">
              <Group gap="sm" mb="xs">
                <ThemeIcon variant="light" color="brand" size={34} radius="md">
                  <ComponentTypeIcon type={c.componentType} size={18} />
                </ThemeIcon>
                <Text fw={600}>{componentTypeLabel(c.componentType)}</Text>
              </Group>
              <Group gap="xl" wrap="wrap">
                {c.componentType === "RECURRING_SUBSCRIPTION" && (
                  <>
                    <Box>
                      <Text size="sm" c="dimmed">Дүн</Text>
                      <MoneyCell value={c.recurringAmount} currency={c.currency} />
                    </Box>
                    <Box>
                      <Text size="sm" c="dimmed">Давтамж</Text>
                      <Text>{c.recurringFrequency}</Text>
                    </Box>
                    <Box>
                      <Text size="sm" c="dimmed">Анкор</Text>
                      <Text>{c.recurringAnchorDay}-нд</Text>
                    </Box>
                  </>
                )}
                {c.componentType === "ONE_TIME_PURCHASE" && (
                  <>
                    <Box>
                      <Text size="sm" c="dimmed">Дүн</Text>
                      <MoneyCell value={c.oneTimeAmount} currency={c.currency} />
                    </Box>
                    {c.oneTimeLicenseExpiresAt && (
                      <Box>
                        <Text size="sm" c="dimmed">Лиценз дуусах</Text>
                        <Text>{formatDate(c.oneTimeLicenseExpiresAt)}</Text>
                      </Box>
                    )}
                  </>
                )}
                {c.componentType === "TRANSACTION_FEE" && (
                  <>
                    <Box>
                      <Text size="sm" c="dimmed">Хувь</Text>
                      <Text style={{ fontVariantNumeric: "tabular-nums" }}>{formatPercent(Number(c.txFeeRate) * 100)}</Text>
                    </Box>
                    {c.txFeeMinAmount && (
                      <Box>
                        <Text size="sm" c="dimmed">Доод</Text>
                        <MoneyCell value={c.txFeeMinAmount} currency={c.currency} />
                      </Box>
                    )}
                    {c.txFeeMaxAmount && (
                      <Box>
                        <Text size="sm" c="dimmed">Дээд</Text>
                        <MoneyCell value={c.txFeeMaxAmount} currency={c.currency} />
                      </Box>
                    )}
                    {c.txFeeApplicableOrderTypes && c.txFeeApplicableOrderTypes.length > 0 && (
                      <Box>
                        <Text size="sm" c="dimmed">Захиалгын төрөл</Text>
                        <Text>{c.txFeeApplicableOrderTypes.map(orderTypeLabel).join(", ")}</Text>
                      </Box>
                    )}
                  </>
                )}
                {c.componentType === "FREE_OR_CUSTOM" && (
                  <Box style={{ flex: 1 }}>
                    <Text size="sm" c="dimmed">Нөхцөл</Text>
                    <Text>{c.customTerms}</Text>
                  </Box>
                )}
              </Group>
            </Card>
          ))}
        </Stack>
      </Stack>

      <Modal opened={termOpen} onClose={() => setTermOpen(false)} title="Гэрээ дуусгах" centered>
        <Stack>
          <Textarea
            label="Шалтгаан"
            required
            minRows={3}
            value={termReason}
            onChange={(e) => setTermReason(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setTermOpen(false)}>Болих</Button>
            <PermissionGate code="BIL_002">
              <Button
                color="red"
                disabled={busy || !termReason.trim()}
                onClick={async () => {
                  await act(
                    () => ChargeContractApi.terminate(contract.id, contract.rowVersion, termReason),
                    "Цуцлахад алдаа"
                  );
                  setTermOpen(false);
                  setTermReason("");
                }}
              >
                Цуцлах
              </Button>
            </PermissionGate>
          </Group>
        </Stack>
      </Modal>

      <ConflictReloadModal
        opened={conflict}
        onReload={() => { clearConflict(); mutate(); }}
      />
    </PageLayout>
  );
};

export default ContractDetail;
